import type { QueryClient } from '@tanstack/react-query';

/**
 * Refresh EVERYTHING the screens hold about one order after an act that changes it in several places at
 * once (approval issues a proforma, settles GST and makes the order payable; a verified payment locks
 * the advance discount and rewrites the totals).
 *
 * The order page reads the order through many separate queries — `['order', id]`, `['order-pay-summary',
 * id]`, `['proforma-status', id]`, `['advance-cd', id]`, `['order-payments', id]`, `['order-timeline', id]`,
 * `['order-shipments', id]`, … — and refreshing only `['order', id]` left the page saying "Proforma: Not
 * issued / Not payable yet" beside "Net payable ₹51,948", or "Balance due ₹51,948 / NOT YET PAID" on an
 * order the approver had just settled, until a manual reload.
 *
 * Matches any `order*` / `proforma*` / `advance-cd*` / `gst-determination*` query that names this order id, so a query added to the
 * order page later is covered without editing this list.
 */
export function refreshOrderEverywhere(qc: QueryClient, orderId: string | number | null | undefined): void {
  const id = orderId == null ? '' : String(orderId);
  if (id) {
    qc.invalidateQueries({
      predicate: (q) => {
        const key = q.queryKey as unknown[];
        const head = typeof key[0] === 'string' ? key[0] : '';
        const orderScoped = head.startsWith('order') || head.startsWith('proforma') || head.startsWith('advance-cd')
          || head.startsWith('gst-determination');
        return orderScoped && key.slice(1).some((k) => String(k) === id);
      },
    });
  }
  // Registers and counters the order appears in.
  ['orders', 'orders-pending-approval', 'payments', 'badges', 'retailer-ledger', 'outstanding-aging']
    .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
}
